import React from 'react';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import HR from '@zg-rentals/multibrand-ui/lib/HR';
import { formatDate } from '@common/paymentsUtils/dateUtils';
import Section from '@rental-manager/components/Section';
import DebouncedButton from '@common/components/DebouncedButton';
import DetailsCard from '@rental-manager/components/DetailsCard';
import SpinnerContainer from '@common/components/SpinnerContainer';
import { pageFlows } from '../pageFlows';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import { getMoveInCost } from '@common/paymentsUtils/moveInCostsUtil';
import { renderDetailRows } from '@rental-manager/components/DetailRows';
import type { MoveInCostsSettingsInterface, NewTermSettings } from '@rental-manager/pages/Dashboard/types';
import { ordinalFormat } from '@common/utils/stringUtils';

type AdditionalConfirmationProps = {
  listingTitle: string;
  moveInCostsSettings: MoveInCostsSettingsInterface;
  onSubmit: () => void;
  goToPage: (...args: any) => void;
};

type Props = NewTermSettings & AdditionalConfirmationProps;

type State = {
  isLoading: boolean;
  showLoadingScreen?: boolean;
};

class NewTenantConfirmation extends React.Component<Props, State> {
  state = {
    isLoading: false,
  };

  onSubmit = async () => {
    await this.showLoadingScreen();
    await this.props.onSubmit();
  };

  showLoadingScreen = (): void => {
    this.setState({ showLoadingScreen: true });
  };

  goToPage = (page) => () => {
    this.props.goToPage(page);
  };

  render(): React.ReactNode {
    const {
      listingTitle,
      tenantFirstName,
      tenantLastName,
      tenantEmail,
      paymentDayOfTheMonth,
      firstDueDate,
      numberOfPayments,
      monthToMonth,
      rentAmount,
      moveInCostsSettings,
    } = this.props;
    const moveInCosts = getMoveInCost(moveInCostsSettings);
    return (
      <>
        {this.state.isLoading && <SpinnerContainer />}
        <Section>
          <Text type="h3">Confirm info and invite tenant</Text>
          <Text>Please review the information you provided for {listingTitle} and confirm it&apos;s correct.</Text>
        </Section>
        <Section>
          <HR />
          <DetailsCard
            className="fs-exclude txtWrap"
            title="Primary tenant"
            onEdit={this.goToPage(pageFlows.NewTenantInfo)}
          >
            {tenantFirstName}
            {tenantLastName}
            {tenantEmail}
          </DetailsCard>
        </Section>
        <Section>
          <HR />
          <DetailsCard
            title="Monthly payment date"
            className="fs-exclude"
            onEdit={this.goToPage(pageFlows.NewTenantDueDate)}
          >
            {`${ordinalFormat(paymentDayOfTheMonth)} of the month`}
          </DetailsCard>
        </Section>
        <Section>
          <HR />
          <DetailsCard
            title="Payment Term"
            className="fs-exclude"
            onEdit={this.goToPage(pageFlows.NewTenantTermLength)}
          >
            {`First payment date: ${formatDate(firstDueDate, 'MMMM dd, yyyy')}`}
            {monthToMonth ? 'No end date/Month-to-Month' : `${numberOfPayments} payments`}
          </DetailsCard>
        </Section>
        <Section>
          <HR />
          <DetailsCard
            title="Base monthly amount"
            className="fs-exclude"
            onEdit={this.goToPage(pageFlows.NewTenantRentAmount)}
          >
            {centsToDollars(rentAmount)}
          </DetailsCard>
        </Section>
        <Section>
          <HR />
          <DetailsCard
            title="Move-in Costs"
            className="fs-exclude"
            onEdit={this.goToPage(pageFlows.NewTenantMoveInCostsPage)}
          >
            {renderDetailRows(moveInCosts)}
          </DetailsCard>
        </Section>
        <Section>
          <HR />
          <DebouncedButton type="primary" full onClick={this.onSubmit}>
            Confirm and invite tenant
          </DebouncedButton>
        </Section>
      </>
    );
  }
}

export default NewTenantConfirmation;
